import React, { useEffect, useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import { api, isRestaurantOpen } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { useCart } from "@/context/CartContext";
import { Star, Clock, Bike, Plus, ShoppingBasket, MessageSquare, Utensils, ShoppingBag, MapPin, Phone } from "lucide-react";
import ProductModal from "@/components/ProductModal";
import { Button } from "@/components/ui/button";
import StarRating from "@/components/StarRating";

const CATEGORY_ORDER = ["Popular", "Starters", "Pizza", "Burgers", "Main Courses", "Family Meals", "Desserts", "Drinks"];
const DAY_NAMES = { mon: "Mon", tue: "Tue", wed: "Wed", thu: "Thu", fri: "Fri", sat: "Sat", sun: "Sun" };

const RestaurantDetail = () => {
  const { id } = useParams();
  const { t, cuisineLabel } = useLang();
  const { setDrawerOpen, count } = useCart();
  const [restaurant, setRestaurant] = useState(null);
  const [menu, setMenu] = useState([]);
  const [selected, setSelected] = useState(null);
  const [reviews, setReviews] = useState({ count: 0, average: null, reviews: [] });

  useEffect(() => {
    api.get(`/restaurants/${id}`).then(r => setRestaurant(r.data));
    api.get(`/restaurants/${id}/menu`).then(r => setMenu(r.data));
    api.get(`/restaurants/${id}/reviews`).then(r => setReviews(r.data));
  }, [id]);

  const grouped = useMemo(() => {
    const map = {};
    menu.forEach(m => {
      const k = m.category;
      if (!map[k]) map[k] = [];
      map[k].push(m);
    });
    return map;
  }, [menu]);

  const orderedCats = useMemo(() => {
    const keys = Object.keys(grouped);
    return [...CATEGORY_ORDER.filter(c => keys.includes(c)), ...keys.filter(c => !CATEGORY_ORDER.includes(c))];
  }, [grouped]);

  if (!restaurant) return <div className="min-h-[50vh] flex items-center justify-center text-navy/50">Loading…</div>;

  const isOpen = restaurant.is_active && isRestaurantOpen(restaurant.opening_hours);

  return (
    <div className="pb-20">
      <div className="relative aspect-[21/9] md:aspect-[21/7] overflow-hidden">
        <img src={restaurant.cover_image} alt={restaurant.name} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-navy/60" />
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 -mt-16 relative">
        <div className="bg-white rounded-3xl border border-navy/10 shadow-xl p-6 lg:p-8">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <h1 className="font-heading text-3xl sm:text-4xl font-extrabold text-navy tracking-tight">{restaurant.name}</h1>
              <p className="text-navy/60 mt-1 capitalize">{cuisineLabel(restaurant.cuisine)} • {restaurant.address}</p>
            </div>
            <div className={`text-xs font-bold uppercase px-3 py-1.5 rounded-full ${isOpen ? "bg-teal text-white" : "bg-navy/80 text-white"}`}>
              {isOpen ? t("open") : t("closed")}
            </div>
          </div>
          <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-navy/70">
            <span className="flex items-center gap-1"><Star className="w-4 h-4 fill-amber-400 text-amber-400" /> <strong className="text-navy">{restaurant.rating}</strong></span>
            <span className="flex items-center gap-1"><Clock className="w-4 h-4" /> {restaurant.delivery_time}</span>
            {restaurant.delivery && <span className="flex items-center gap-1" title={t("deliveryTiered")}><Bike className="w-4 h-4" /> €2.99 / €4.99</span>}
            {restaurant.delivery && <span>Min. €{restaurant.min_order.toFixed(2)}</span>}
            {restaurant.price_range && <span><strong className="text-navy">{restaurant.price_range}</strong></span>}
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            {restaurant.dine_in && <span className="inline-flex items-center gap-1 text-xs font-semibold text-teal bg-teal/10 px-2.5 py-1 rounded-full"><Utensils className="w-3 h-3" /> Dine-in</span>}
            {restaurant.takeaway && <span className="inline-flex items-center gap-1 text-xs font-semibold text-ocean bg-ocean/10 px-2.5 py-1 rounded-full"><ShoppingBag className="w-3 h-3" /> Takeaway</span>}
            {restaurant.delivery && <span className="inline-flex items-center gap-1 text-xs font-semibold text-coral bg-coral/10 px-2.5 py-1 rounded-full"><Bike className="w-3 h-3" /> Delivery</span>}
          </div>
          <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-navy/60">
            {restaurant.address && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {restaurant.address}</span>}
            {restaurant.phone && <a href={`tel:${restaurant.phone}`} className="flex items-center gap-1 hover:text-coral"><Phone className="w-3 h-3" /> {restaurant.phone}</a>}
          </div>
          {restaurant.description && <p className="mt-3 text-navy/70 text-sm">{restaurant.description}</p>}

          <details className="mt-4 group">
            <summary className="text-sm font-semibold text-navy cursor-pointer hover:text-coral">Opening hours</summary>
            <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-1 text-xs">
              {Object.entries(restaurant.opening_hours || {}).map(([d, h]) => (
                <div key={d} className="flex justify-between p-2 bg-sand-50 rounded-lg">
                  <span className="font-medium">{DAY_NAMES[d] || d}</span>
                  <span className="text-navy/60">{h.open}–{h.close}</span>
                </div>
              ))}
            </div>
          </details>
        </div>

        <div className="mt-8">
          {orderedCats.map(cat => (
            <div key={cat} className="mb-10">
              <h2 className="font-heading text-2xl font-bold text-navy mb-4" data-testid={`menu-cat-${cat}`}>{cat}</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {grouped[cat].map((item) => (
                  <button
                    key={item.id}
                    onClick={() => isOpen && setSelected(item)}
                    disabled={!isOpen}
                    data-testid={`menu-item-${item.id}`}
                    className="flex gap-3 p-3 rounded-2xl bg-white border border-navy/8 text-left card-hover disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="font-heading font-bold text-navy">{item.name}</h3>
                        {item.is_popular && <span className="text-[9px] uppercase font-bold text-coral bg-coral/10 px-1.5 py-0.5 rounded">Popular</span>}
                      </div>
                      {item.description && <p className="text-xs text-navy/60 mt-1 line-clamp-2">{item.description}</p>}
                      <div className="mt-2 font-semibold text-navy">€{item.price.toFixed(2)}</div>
                    </div>
                    {item.image && (
                      <div className="relative shrink-0">
                        <img src={item.image} alt={item.name} className="w-24 h-24 rounded-xl object-cover" />
                        {isOpen && (
                          <div className="absolute -bottom-2 -right-2 w-8 h-8 rounded-full bg-coral text-white flex items-center justify-center shadow-lg">
                            <Plus className="w-4 h-4" />
                          </div>
                        )}
                      </div>
                    )}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Public reviews section */}
        <div className="mt-12 pb-8">
          <div className="flex items-center gap-2 mb-4">
            <MessageSquare className="w-5 h-5 text-coral" />
            <h2 className="font-heading text-2xl font-bold text-navy">Reviews</h2>
            {reviews.count > 0 && <span className="text-navy/60 text-sm">({reviews.count})</span>}
          </div>
          {reviews.count === 0 ? (
            <p className="text-navy/50 text-sm">No reviews yet — be the first to share your experience.</p>
          ) : (
            <>
              <div className="flex items-center gap-3 mb-4">
                <StarRating value={Math.round(reviews.average || 0)} readOnly size="md" />
                <span className="font-heading font-bold text-navy text-lg">{reviews.average}</span>
                <span className="text-navy/50 text-sm">out of 5</span>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                {reviews.reviews.map((r, i) => (
                  <div key={i} className="bg-white rounded-2xl border border-navy/8 p-4" data-testid={`review-${i}`}>
                    <div className="flex items-center justify-between">
                      <div className="font-heading font-bold text-navy">{r.customer_first_name || "Guest"}</div>
                      <StarRating value={r.restaurant_rating} readOnly size="sm" />
                    </div>
                    <div className="text-[11px] text-navy/50 mt-0.5">{new Date(r.created_at).toLocaleDateString()}</div>
                    {r.restaurant_comment && <p className="text-sm text-navy/80 mt-2 leading-relaxed">"{r.restaurant_comment}"</p>}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {isOpen && count > 0 && (
        <div className="fixed bottom-4 left-4 right-4 lg:hidden z-30">
          <Button onClick={() => setDrawerOpen(true)} className="btn-coral w-full h-14 shadow-2xl" data-testid="view-basket-mobile">
            <ShoppingBasket className="w-5 h-5 mr-2" /> {t("basket")} ({count})
          </Button>
        </div>
      )}

      <ProductModal open={!!selected} onClose={() => setSelected(null)} item={selected} restaurant={restaurant} />
    </div>
  );
};

export default RestaurantDetail;
